import { useRestApiProvider } from "@jugl-web/rest-api";
import { endOfMonth, startOfMonth } from "date-fns";
import { FC, ReactNode, useMemo } from "react";
import { useChatsByCountriesWidgetData } from "../hooks/useChatsByCountriesWidgetData";
import { useImpactDistributionWidgetData } from "../hooks/useImpactDistributionWidgetData";
import { useImpactOverviewWidgetData } from "../hooks/useImpactOverviewWidgetData";
import { useSentimentAnalysisWidgetData } from "../hooks/useSentimentAnalysisWidgetData";
import { useTopicDistributionWidgetData } from "../hooks/useTopicDistributionWidgetData";
import {
  AgentAnalyticsContext,
  AgentAnalyticsContextValue,
} from "./AgentAnalyticsContext";

interface AgentAnalyticsProviderProps {
  children: ReactNode;
  entityId: string;
}

export const AgentAnalyticsProvider: FC<AgentAnalyticsProviderProps> = ({
  children,
  entityId,
}) => {
  const { integrationsApi } = useRestApiProvider();

  const timeRange = useMemo(() => {
    const now = new Date();

    return {
      fromISO: startOfMonth(now).toISOString(),
      toISO: endOfMonth(now).toISOString(),
    };
  }, []);

  const { data, isLoading, isFetching, isError, refetch } =
    integrationsApi.useGetAIAgentAnalyticsQuery({
      entityId,
      fromISO: timeRange.fromISO,
      toISO: timeRange.toISO,
    });

  const analyticsData = useMemo(() => data ?? [], [data]);

  const chatsByCountriesData = useChatsByCountriesWidgetData({ analyticsData });

  const impactOverviewWidgetData = useImpactOverviewWidgetData({
    analyticsData,
  });

  const { impactDistributionData, impactDetailsById } =
    useImpactDistributionWidgetData({ analyticsData });

  const topicDistributionData = useTopicDistributionWidgetData({
    analyticsData,
  });

  const sentimentAnalysisData = useSentimentAnalysisWidgetData({
    analyticsData,
  });

  const contextValue = useMemo<AgentAnalyticsContextValue>(
    () => ({
      chatsByCountriesData,
      impactOverviewWidgetData,
      impactDistributionData,
      impactDetailsById,
      topicDistributionData,
      sentimentAnalysisData,
      timeRange,
      isLoading: isLoading || isFetching,
      isError,
      refetch,
    }),
    [
      chatsByCountriesData,
      impactOverviewWidgetData,
      impactDistributionData,
      impactDetailsById,
      topicDistributionData,
      sentimentAnalysisData,
      timeRange,
      isLoading,
      isFetching,
      isError,
      refetch,
    ]
  );

  return (
    <AgentAnalyticsContext.Provider value={contextValue}>
      {children}
    </AgentAnalyticsContext.Provider>
  );
};
